import React, { useEffect, useState, useRef } from "react";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Printer, MapPin, Upload, Camera, RefreshCw, Plus, Trash2, BadgeCheck, Download } from "lucide-react";
import { Input, TextArea, FilePicker, Select } from "./Warehouse";

const getLoc = () => new Promise((res, rej) => {
  if (!navigator.geolocation) return rej(new Error("Geolokasi tidak didukung"));
  navigator.geolocation.getCurrentPosition(p => res({ lat: p.coords.latitude, lng: p.coords.longitude }), rej, { enableHighAccuracy: true, timeout: 15000 });
});

export default function Resi() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [branches, setBranches] = useState([]);
  const [products, setProducts] = useState([]);

  const load = async () => setRows((await api.get("/branch/resi")).data);
  useEffect(() => {
    load();
    api.get("/products").then(({ data }) => setProducts(data));
    if (user && user.role !== "cabang") api.get("/branches").then(({ data }) => setBranches(data));
  }, [user]);

  const canManage = user?.role === "admin" || user?.role === "keuangan";

  return (
    <div className="space-y-5">
      <div>
        <p className="text-xs uppercase tracking-widest text-blue-600 font-bold">Modul Resi & Drop-Off</p>
        <h1 className="text-3xl font-extrabold text-slate-900" style={{fontFamily:'Manrope'}}>Resi Pengiriman</h1>
        <p className="text-sm text-slate-600 mt-1">Upload PDF (1–50 lembar) — sistem membaca detail orderan beserta qty dari area bawah resi otomatis (mendukung resi scan via OCR).</p>
      </div>

      <UploadResiForm onDone={load} branches={branches} canPickBranch={canManage} />

      <div>
        <h2 className="text-lg font-bold text-slate-900 mb-3" style={{fontFamily:'Manrope'}}>Daftar Resi</h2>
        <div className="space-y-3">
          {rows.length === 0 && <p className="text-sm text-slate-500">Belum ada resi.</p>}
          {rows.map((r) => <ResiCard key={r.id} r={r} onDone={load} products={products} canManage={canManage} role={user?.role} branches={branches} />)}
        </div>
      </div>
    </div>
  );
}

function UploadResiForm({ onDone, branches, canPickBranch }) {
  const [orderRef, setOrderRef] = useState("");
  const [courier, setCourier] = useState("JNT");
  const [notes, setNotes] = useState("");
  const [branchId, setBranchId] = useState("");
  const [file, setFile] = useState(null);
  const fileRef = useRef();

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("File PDF resi wajib");
    if (canPickBranch && !branchId) return toast.error("Pilih cabang tujuan");
    const fd = new FormData();
    fd.append("order_ref", orderRef); fd.append("courier", courier); fd.append("notes", notes);
    if (branchId) fd.append("branch_id", branchId);
    fd.append("pdf", file);
    const t = toast.loading("Mengunggah & menganalisis PDF...");
    try {
      const { data } = await api.post("/branch/resi/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const n = (data.parsed_items || []).reduce((a, b) => a + b.qty, 0);
      toast.success(`Resi tersimpan: ${data.page_count || 0} lembar, ${n} produk terdeteksi otomatis`, { id: t });
      setOrderRef(""); setNotes(""); setBranchId(""); setFile(null); fileRef.current.value = "";
      onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal", { id: t }); }
  };

  return (
    <form onSubmit={submit} className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
      <h3 className="font-bold text-lg" style={{fontFamily:'Manrope'}}>Upload Resi Baru</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input testid="resi-order" label="No. Order (opsional)" value={orderRef} onChange={setOrderRef} />
        <Select testid="resi-courier" label="Kurir" value={courier} onChange={setCourier} options={[
          {value:"SPX",label:"SPX / Shopee Express"},{value:"JNT",label:"JNT"},{value:"JNE",label:"JNE"},{value:"SiCepat",label:"SiCepat"},{value:"Anteraja",label:"Anteraja"},{value:"Ninja",label:"Ninja"},{value:"Lainnya",label:"Lainnya"}]} required />
      </div>
      {canPickBranch && (
        <Select testid="resi-branch" label="Ditujukan ke Cabang" value={branchId} onChange={setBranchId} options={branches.map(b => ({ value: b.id, label: `${b.name} - ${b.city}` }))} required />
      )}
      <TextArea testid="resi-notes" label="Catatan" value={notes} onChange={setNotes} />
      <div>
        <span className="text-xs font-bold uppercase tracking-wider text-slate-600">File Resi (PDF, 1–50 lembar)</span>
        <input ref={fileRef} data-testid="resi-pdf" type="file" accept="application/pdf" hidden onChange={(e) => setFile(e.target.files[0])} />
        <button type="button" onClick={() => fileRef.current.click()} className="mt-1 w-full h-16 border-2 border-dashed border-blue-300 bg-blue-50 text-blue-700 rounded-xl flex flex-col items-center justify-center gap-1 active:bg-blue-100 transition-colors">
          <Upload size={20} />
          <span className="text-sm font-semibold">{file ? file.name : "Pilih PDF"}</span>
        </button>
      </div>
      <button data-testid="resi-submit" type="submit" className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl active:scale-[0.98] transition-transform">Simpan & Analisis</button>
    </form>
  );
}

function ResiCard({ r, onDone, products, canManage, role, branches }) {
  const [dropOpen, setDropOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [items, setItems] = useState(r.parsed_items || []);
  const [notes, setNotes] = useState(""); const [file, setFile] = useState(null);
  const fileRef = useRef();
  const [confirmAction, setConfirmAction] = useState(null); // "cancel" | "delete"

  useEffect(() => { setItems(r.parsed_items || []); }, [r.parsed_items]);

  const branchName = branches.find(b => b.id === r.branch_id)?.name;

  const printResi = async () => {
    window.open(fileUrl(r.pdf_id), "_blank");
    if (!r.printed) {
      try { await api.post(`/branch/resi/${r.id}/print`); onDone(); } catch {}
    }
  };

  const reparse = async () => {
    const t = toast.loading("Menganalisis ulang PDF...");
    try {
      const { data } = await api.post(`/resi/${r.id}/parse`);
      toast.success(`Terdeteksi ${(data.parsed_items || []).reduce((a,b) => a + b.qty, 0)} produk dari ${data.page_count || 0} lembar`, { id: t });
      onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal", { id: t }); }
  };

  const saveItems = async () => {
    try {
      await api.patch(`/resi/${r.id}/items`, { items: items.filter(i => i.product_id && Number(i.qty) > 0) });
      toast.success("Item tersimpan"); setEditOpen(false); onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal"); }
  };

  const confirmSales = async () => {
    const t = toast.loading("Mengurangi stok cabang...");
    try {
      await api.post(`/resi/${r.id}/confirm-sales`);
      toast.success("Penjualan dikonfirmasi — stok berkurang & masuk omset", { id: t });
      setConfirmAction(null);
      onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal", { id: t }); }
  };

  const cancelSales = async () => {
    const t = toast.loading("Membatalkan penjualan & mengembalikan stok...");
    try {
      await api.post(`/resi/${r.id}/cancel-sales`);
      toast.success("Penjualan dibatalkan — stok dikembalikan & omset dikoreksi. Item bisa dikoreksi lalu dikonfirmasi ulang.", { id: t });
      setConfirmAction(null);
      onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal", { id: t }); }
  };

  const deleteResi = async () => {
    const t = toast.loading("Menghapus resi...");
    try {
      await api.delete(`/resi/${r.id}`);
      toast.success("Resi dihapus. Omset & stok otomatis dikoreksi.", { id: t });
      setConfirmAction(null);
      onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal", { id: t }); }
  };

  const submitDrop = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Foto drop-off wajib");
    const t = toast.loading("Mengambil GPS...");
    try {
      const { lat, lng } = await getLoc();
      toast.loading("Menyimpan...", { id: t });
      const fd = new FormData();
      fd.append("lat", lat); fd.append("lng", lng); fd.append("notes", notes); fd.append("photo", file);
      await api.post(`/branch/resi/${r.id}/dropoff`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Drop-off tercatat!", { id: t });
      onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || err.message || "Gagal", { id: t }); }
  };

  const statusBadge = r.dropped_off
    ? <span className="px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-bold uppercase tracking-wider">Terkirim</span>
    : r.printed
    ? <span className="px-2.5 py-1 rounded-full bg-blue-100 text-blue-800 text-[10px] font-bold uppercase tracking-wider">Diprint</span>
    : <span className="px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-wider">Baru</span>;

  const totalQty = (r.parsed_items || []).reduce((a, b) => a + b.qty, 0);

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            {statusBadge}
            <span className="text-xs font-bold text-slate-700">{r.courier}</span>
            {r.sales_confirmed && <span className="px-2.5 py-1 rounded-full bg-violet-100 text-violet-800 text-[10px] font-bold uppercase tracking-wider">Penjualan Terkonfirmasi</span>}
          </div>
          <p className="font-semibold text-slate-900" style={{fontFamily:'Manrope'}}>{r.order_ref || "Tanpa No. Order"}</p>
          <p className="text-xs text-slate-500">{new Date(r.created_at).toLocaleString('id-ID')} · oleh {r.created_by_name}{branchName ? ` · ${branchName}` : ''}</p>
          {r.notes && <p className="text-xs text-slate-600 mt-1">{r.notes}</p>}
        </div>
        {r.dropoff_photo_id && <img src={fileUrl(r.dropoff_photo_id)} alt="" className="w-16 h-16 rounded-lg object-cover" />}
      </div>

      <div className="mt-3 border-t border-slate-100 pt-3">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-600">Produk Terjual (otomatis)</p>
          <span className="text-xs text-slate-500">{r.page_count || 0} lembar · total {totalQty} pcs</span>
        </div>
        {(!r.parsed_items || r.parsed_items.length === 0) && !editOpen && (
          <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            Tidak ada produk terdeteksi dari PDF (kemungkinan resi berupa gambar/scan). Gunakan Koreksi Manual.
          </p>
        )}
        {!editOpen ? (
          <div className="mt-2 space-y-1">
            {(r.parsed_items || []).map((it) => (
              <div key={it.product_id} className="flex justify-between gap-2 text-sm">
                <div className="min-w-0">
                  <p className="text-slate-800 truncate">{it.name}</p>
                  <p className="text-xs text-slate-400 font-mono">{[it.sku, it.pages ? `${it.pages} resi` : null].filter(Boolean).join(" · ") || "-"}</p>
                </div>
                <span className="font-bold text-slate-900 flex-shrink-0" style={{fontFamily:'IBM Plex Sans'}}>× {it.qty}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-2 space-y-2">
            {items.map((it, idx) => (
              <div key={idx} className="flex gap-2 items-center">
                <select data-testid={`item-prod-${r.id}-${idx}`} value={it.product_id} onChange={(e) => { const c = [...items]; c[idx].product_id = e.target.value; setItems(c); }} className="flex-1 h-11 px-3 rounded-lg border border-slate-300 bg-white text-sm min-w-0">
                  <option value="">-- produk --</option>
                  {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <input data-testid={`item-qty-${r.id}-${idx}`} type="number" min={1} value={it.qty} onChange={(e) => { const c = [...items]; c[idx].qty = parseInt(e.target.value) || 0; setItems(c); }} className="w-20 h-11 px-3 rounded-lg border border-slate-300 text-sm" />
                <button type="button" onClick={() => setItems(items.filter((_, i) => i !== idx))} className="w-11 h-11 rounded-lg text-red-600 hover:bg-red-50 flex items-center justify-center flex-shrink-0"><Trash2 size={16} /></button>
              </div>
            ))}
            <button type="button" data-testid={`add-item-${r.id}`} onClick={() => setItems([...items, { product_id: "", qty: 1 }])} className="h-10 px-3 rounded-lg border border-dashed border-slate-300 text-sm font-semibold text-slate-600 flex items-center gap-1"><Plus size={14} /> Tambah Item</button>
            <button data-testid={`save-items-${r.id}`} onClick={saveItems} className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-lg active:scale-[0.98] transition-transform">Simpan Koreksi</button>
          </div>
        )}
        {(r.unmatched_rows || []).length > 0 && (
          <div className="mt-2 text-xs bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-red-700">
            <p className="font-bold">{r.unmatched_rows.length} baris orderan tidak cocok dengan produk manapun — koreksi manual atau lengkapi nama produk di menu Produk:</p>
            {r.unmatched_rows.map((u, i) => <p key={i} className="truncate mt-0.5">• {u}</p>)}
          </div>
        )}
        <div className="mt-3 flex flex-wrap gap-2">
          {!r.sales_confirmed && (
            <>
              <button data-testid={`reparse-${r.id}`} onClick={reparse} className="h-10 px-3 rounded-lg border border-slate-200 text-sm font-semibold text-slate-700 flex items-center gap-1 hover:bg-slate-50"><RefreshCw size={14} /> Hitung Ulang</button>
              <button data-testid={`edit-items-${r.id}`} onClick={() => setEditOpen(!editOpen)} className="h-10 px-3 rounded-lg border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50">{editOpen ? "Tutup" : "Koreksi Manual"}</button>
            </>
          )}
          {canManage && !r.sales_confirmed && (
            confirmAction === "confirm" ? (
              <span className="flex gap-2 items-center flex-wrap">
                <button data-testid={`confirm-sales-yes-${r.id}`} onClick={confirmSales} className="h-10 px-3 rounded-lg bg-violet-700 text-white text-sm font-bold">Yakin konfirmasi? Stok berkurang & masuk omset</button>
                <button data-testid={`confirm-sales-no-${r.id}`} onClick={() => setConfirmAction(null)} className="h-10 px-3 rounded-lg border border-slate-300 text-xs font-bold text-slate-600">Batal</button>
              </span>
            ) : (
              <button data-testid={`confirm-sales-${r.id}`} onClick={() => setConfirmAction("confirm")} className="h-10 px-3 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-bold flex items-center gap-1 active:scale-[0.98] transition-transform"><BadgeCheck size={14} /> Konfirmasi Penjualan</button>
            )
          )}
          {r.sales_confirmed && <span className="h-10 px-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm font-bold flex items-center gap-1"><BadgeCheck size={14} /> Stok sudah dikurangi</span>}
        </div>
      </div>

      {canManage && (
        <div className="mt-2 flex flex-wrap gap-2">
          {r.sales_confirmed && (
            confirmAction === "cancel" ? (
              <span className="flex gap-2 items-center flex-wrap">
                <button data-testid={`confirm-cancel-${r.id}`} onClick={cancelSales} className="h-9 px-3 rounded-lg bg-amber-600 text-white text-xs font-bold">Yakin batalkan penjualan?</button>
                <button data-testid={`abort-cancel-${r.id}`} onClick={() => setConfirmAction(null)} className="h-9 px-3 rounded-lg border border-slate-300 text-xs font-bold text-slate-600">Batal</button>
              </span>
            ) : (
              <button data-testid={`cancel-sales-${r.id}`} onClick={() => setConfirmAction("cancel")} className="h-9 px-3 rounded-lg border border-amber-300 text-amber-700 text-xs font-bold hover:bg-amber-50">Batalkan Penjualan</button>
            )
          )}
          {confirmAction === "delete" ? (
            <span className="flex gap-2 items-center flex-wrap">
              <button data-testid={`confirm-delete-${r.id}`} onClick={deleteResi} className="h-9 px-3 rounded-lg bg-red-600 text-white text-xs font-bold">Yakin hapus resi ini?</button>
              <button data-testid={`abort-delete-${r.id}`} onClick={() => setConfirmAction(null)} className="h-9 px-3 rounded-lg border border-slate-300 text-xs font-bold text-slate-600">Batal</button>
            </span>
          ) : (
            <button data-testid={`delete-resi-${r.id}`} onClick={() => setConfirmAction("delete")} className="h-9 px-3 rounded-lg border border-red-300 text-red-600 text-xs font-bold hover:bg-red-50 flex items-center gap-1"><Trash2 size={13} /> Hapus Resi</button>
          )}
        </div>
      )}

      <div className="mt-3 grid grid-cols-3 gap-2 border-t border-slate-100 pt-3">
        <button data-testid={`print-${r.id}`} onClick={printResi} className="h-11 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-lg flex items-center justify-center gap-2 text-sm active:scale-[0.98] transition-transform">
          <Printer size={16} /> Print
        </button>
        <a data-testid={`download-${r.id}`} href={`${fileUrl(r.pdf_id)}&download=1`} download className="h-11 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg flex items-center justify-center gap-2 text-sm active:scale-[0.98] transition-transform">
          <Download size={16} /> Unduh
        </a>
        {role !== "keuangan" && !r.dropped_off ? (
          <button data-testid={`open-drop-${r.id}`} onClick={() => setDropOpen(!dropOpen)} className="h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg flex items-center justify-center gap-2 text-sm active:scale-[0.98] transition-transform">
            <Camera size={16} /> Drop-off
          </button>
        ) : r.dropped_off ? (
          <div className="h-11 bg-emerald-50 text-emerald-700 rounded-lg flex items-center justify-center gap-2 text-sm font-bold border border-emerald-200">
            <MapPin size={14} /> Sudah drop
          </div>
        ) : (
          <div className="h-11 bg-slate-50 text-slate-500 rounded-lg flex items-center justify-center gap-2 text-sm font-semibold border border-slate-200">
            Menunggu cabang
          </div>
        )}
      </div>
      {dropOpen && !r.dropped_off && (
        <form onSubmit={submitDrop} className="mt-4 space-y-3 border-t border-slate-100 pt-4">
          <TextArea testid={`drop-notes-${r.id}`} label="Catatan Drop-off" value={notes} onChange={setNotes} />
          <FilePicker testid={`drop-photo-${r.id}`} label="Foto Bukti Drop-off (di lokasi kurir)" file={file} setFile={setFile} inputRef={fileRef} />
          <button data-testid={`submit-drop-${r.id}`} type="submit" className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg">Simpan Drop-off (dengan GPS)</button>
        </form>
      )}
    </div>
  );
}

